// ================================================================================================
// Localization
// --------
// Global subsystem for localization translation
//
// TODO: Mark the changes labels as a modified and have UI presentation of them
// ================================================================================================
import _ from "lodash";
import { JSONSafeStringify, isDefined } from "@Utils/Micro";
import { DebugEx } from "@Utils/DebugEx";
import { WebNode } from '@API/WebNode';
import { setGlobal } from '@Utils/Globals';

import { ImageViewerStatics } from '../ImageViewer';
import { ReactTableStatics } from '@UI/Tables/ReactTable';
import { CalendarLocalizationTextMapKey, GetCalendarLocalizationDictionary } from '@Primitive/CalendarLocalization';
import { CVViewerWidgetMapKey, GetCVViewerWidgetLocalizationDictionary } from '@UI/Widgets/Things/Image/CVViewerLocalization';
import { GetSelectLocalizationDictionary, SelectLocalizationTextMapKey } from '@Primitive/SelectLocalization';
import { GetQueryBuilderLocalizationDictionary, QueryBuilderLocalizationTextMapKey } from '@Primitive/QueryBuilderLocalization';
import { GetVideoPlayerLocalizationDictionary, VideoPlayerLocalizationTextMapKey } from '@UI/Widgets/Things/Image/VideoPlayerLocalization';
import { LeafletMapLocalizationDictionary, LeafletMapLocalizationTextMapKey } from '@Primitive/Map/LeafletMapLocalization';
import { SearchBarGetLocalizationDictionary, SearchBarLocalizationTextMapKey } from '@Primitive/SearchBarLocalization';
import { GetRsDateRangePickerLocalizationDictionary, RsDateRangePickerLocalizationTextMapKey } from '@Primitive/RsDateRangePickerLocalization';
import { yHas } from "@Utils/Utils";
// ================================================================================================
const __LOCALIZATION_STORAGE_VERSION__ = "0.0.1";
export interface LocalizationSettings {
    /**
     * Version of the settings scheme.
     */
    Version: string;

    /**
     * The selected language based on ISO 639-2 codes
     * http://www.loc.gov/standards/iso639-2/php/code_list.php
     */
    SelectedLanguage: string;
}

export const LocalizationDefaultKey = "default";
// ================================================================================================
export type LocalizationMap = { [key: string]: string };
export type LocalizationDictionaryMap = { [key: string]: LocalizationMap };
// ================================================================================================
export interface LocalizationDictionary {
    /**
     * Language Dictionary
     * Dictionary[GroupKey][Key]
     * e.x. Dictionary[WidgetThingKey][Key]
     */
    Dictionary: LocalizationDictionaryMap;

    /**
     * Language default overrides
     */
    DictionaryOverrides?: LocalizationDictionaryMap;

    /**
     * Language UI info
     */
    Info: LocalizationLanguageInfo;
}
// ================================================================================================
export interface LocalizationLanguageInfo {
    /**
     * Language code based on ISO 639-2.
     * http://www.loc.gov/standards/iso639-2/php/code_list.php
     */
    Code: string;

    /**
     * UI label
     */
    Label: string;

    /**
     * Flag icon based on
     */
    FlagIcon: string;

    /**
     * Enable the showing of the language
     */
    IsEnabled: boolean;

    /**
     * Set if this is the default
     */
    IsDefault: boolean;
}
// ================================================================================================
export interface LocalizationChangeLanguageReq {
    /**
     * Language string
     */
    Language: string;
    /**
     * Select whether board should be reloaded
     */
    ReloadBoard: boolean;
}
// ================================================================================================
export class Localization {
    // --------------------------------------------------------------------------------------------
    private static _instanceDict: { [key: string]: Localization } = {};
    public static GetInstance(key: string): Localization {
        return _.has(this._instanceDict, key) ? this._instanceDict[key] : null;
    }
    // --------------------------------------------------------------------------------------------
    public static TryGetPageInfo(gkey: string): { Name: string, key: string } {
        if (gkey == "Primitive_DashboardManager")
            return { Name: "Board", key: gkey };
        if (_.startsWith(gkey, "Primitive"))
            return { Name: "Primitive", key: gkey };
        return null;
    }
    // --------------------------------------------------------------------------------------------
    public get SelectedLanguage(): string { return this.Settings.SelectedLanguage }
    public PendingSaveChanges: boolean;
    private Settings: LocalizationSettings;
    private key: string;
    private Languages: { [code: string]: LocalizationDictionary } = {};
    private CacheLanguages: { [code: string]: LocalizationDictionary } = {};

    /**
     * Default language for the designer.
     */
    public DefaultLanguage: string;
    constructor(key: string, selectedLanguage: string) {
        this.key = key;
        this.PendingSaveChanges = false;

        // Set global access to dictionary
        setGlobal("Localization", Localization._instanceDict);

        // Detect if we already have instance with the same key
        if (_.has(Localization._instanceDict, key)) {
            DebugEx.TraceError("Try to init a second time the same localization", key);
        }

        // Link the singleton of this constractor to the generic map
        Localization._instanceDict[key] = this;

        // ------------------------------ +++ temporary changes
        const initialDictionary = {};
        // fill dictionary with primitives
        initialDictionary[ImageViewerStatics.LocalizationTextMapKey] = ImageViewerStatics.GetLocalizationDictionary();
        initialDictionary[LeafletMapLocalizationTextMapKey] = LeafletMapLocalizationDictionary;
        initialDictionary[ReactTableStatics.LocalizationTextMapKey] = ReactTableStatics.GetLocalizationDictionary();
        initialDictionary[CVViewerWidgetMapKey] = GetCVViewerWidgetLocalizationDictionary();
        initialDictionary[SearchBarLocalizationTextMapKey] = SearchBarGetLocalizationDictionary();
        initialDictionary[VideoPlayerLocalizationTextMapKey] = GetVideoPlayerLocalizationDictionary();
        initialDictionary[QueryBuilderLocalizationTextMapKey] = GetQueryBuilderLocalizationDictionary();
        initialDictionary[SelectLocalizationTextMapKey] = GetSelectLocalizationDictionary();
        initialDictionary[CalendarLocalizationTextMapKey] = GetCalendarLocalizationDictionary();
        initialDictionary[RsDateRangePickerLocalizationTextMapKey] = GetRsDateRangePickerLocalizationDictionary();
        // ------------------------------ --- temporary changes

        // Init default language
        this.DefaultLanguage = LocalizationDefaultKey;
        this.Languages[LocalizationDefaultKey] = {
            Info: {
                Code: LocalizationDefaultKey,
                Label: "English",
                FlagIcon: "svg/flags/united states",
                IsEnabled: true,
                IsDefault: this.DefaultLanguage == LocalizationDefaultKey
            },
            Dictionary: _.cloneDeep(initialDictionary)
        };

        this.Settings = JSON.parse(sessionStorage.getItem("LocalizationSettings_" + this.key));
        if (this.Settings == null ||
            this.Settings.Version != __LOCALIZATION_STORAGE_VERSION__) {
            // Default values
            this.Settings = {
                Version: __LOCALIZATION_STORAGE_VERSION__,
                SelectedLanguage: selectedLanguage
            };
            this.SaveSettings();
        }

        // Send signal to WebNode to update a common key 
        let webNode = WebNode.GetInstanceKey(this.key);
        if (isDefined(webNode) && !webNode.DummyNode) {
            webNode.UpdateLanguageCode(this.SelectedLanguage);
        }
    }
    // --------------------------------------------------------------------------------------------
    private InvalidCacheLang(lang: string) {
        delete this.CacheLanguages[lang];
    }
    // --------------------------------------------------------------------------------------------
    private SaveSettings() {
        sessionStorage.setItem("LocalizationSettings_" + this.key, JSONSafeStringify(this.Settings));
    }
    // --------------------------------------------------------------------------------------------
    private InitLanguage(langCode: string) {
        this.Languages[langCode] = {
            Info: { Code: langCode, Label: langCode, FlagIcon: "", IsEnabled: true, IsDefault: false }, // TODO: find the label
            Dictionary: {},
            DictionaryOverrides: {}
        };
    }
    // --------------------------------------------------------------------------------------------
    /**
     * Change the Selected language
     */
    public ChangeSelectedLanguage(changeReq: LocalizationChangeLanguageReq) {
        // Check that the language exist
        if (!_.has(this.Languages, changeReq.Language))
            return;

        if (this.Settings.SelectedLanguage != changeReq.Language) {
            // Save the settings to browser storage
            this.Settings.SelectedLanguage = changeReq.Language;
            this.SaveSettings();

            // Refresh the page
            if (changeReq.ReloadBoard == true) {
                document.location.reload();
            }
        }
    }
    // --------------------------------------------------------------------------------------------
    /**
     * Add text to dictionary for specific language
     */
    public AddTranslateText(langCode: string, groupKey: string, subKey: string, text: string) {
        // Init language
        if (!_.has(this.Languages, langCode))
            this.InitLanguage(langCode);

        if (langCode == LocalizationDefaultKey) {
            // Init dictionary
            if (this.Languages[langCode].DictionaryOverrides == null)
                this.Languages[langCode].DictionaryOverrides = {};

            if (!_.has(this.Languages[langCode].DictionaryOverrides, groupKey))
                this.Languages[langCode].DictionaryOverrides[groupKey] = {};

            // Add dictionary
            this.Languages[langCode].DictionaryOverrides[groupKey][subKey] = text;
        } else {
            // Init dictionary
            if (!_.has(this.Languages[langCode].Dictionary, groupKey))
                this.Languages[langCode].Dictionary[groupKey] = {};

            // Add dictionary
            this.Languages[langCode].Dictionary[groupKey][subKey] = text;
        }
        this.InvalidCacheLang(langCode);
    }
    // --------------------------------------------------------------------------------------------
    public ReplaceTexts(langCode: string, groupKey: string, texts: LocalizationMap) {
        // Init language
        if (!_.has(this.Languages, langCode))
            this.InitLanguage(langCode);
        if (!_.has(this.Languages[langCode].Dictionary, groupKey))
            this.Languages[langCode].Dictionary[groupKey] = {};

        // replace the texts
        this.Languages[langCode].Dictionary[groupKey] = texts;
        this.InvalidCacheLang(langCode);
    }
    // --------------------------------------------------------------------------------------------
    /**
     * Get the text for the selected language
     */
    public GetTextMap(groupKey: string): LocalizationMap {
        let lang = this.Settings.SelectedLanguage;
        if (!_.has(this.Languages, this.Settings.SelectedLanguage))
            lang = this.DefaultLanguage;

        if (!_.has(this.Languages[lang].Dictionary, groupKey) &&
            !_.has(this.Languages[this.DefaultLanguage].Dictionary, groupKey))
            return {};

        // if is exist return the cached one
        if (_.has(this.CacheLanguages, lang) &&
            _.has(this.CacheLanguages[lang].Dictionary, groupKey))
            return this.CacheLanguages[lang].Dictionary[groupKey];

        // If the cache is not have the language then copy it from the default 
        if (!yHas(this.CacheLanguages, lang))
            this.CacheLanguages[lang] = _.cloneDeep(this.Languages[lang]);

        // Fill the cache and return it;
        for (let key of _.keys(this.Languages[this.DefaultLanguage].Dictionary)) {
            this.CacheLanguages[lang].Dictionary[key] = _.assign({},
                this.Languages[this.DefaultLanguage].Dictionary[key],
                _.has(this.Languages[this.DefaultLanguage].DictionaryOverrides, key) ? this.Languages[this.DefaultLanguage].DictionaryOverrides[key] : {},
                _.has(this.Languages[lang].Dictionary, key) ? this.Languages[lang].Dictionary[key] : {},
                _.has(this.Languages[lang].DictionaryOverrides, key) ? this.Languages[lang].DictionaryOverrides[key] : {},
            );
        }

        return this.CacheLanguages[lang].Dictionary[groupKey];
    }
    // --------------------------------------------------------------------------------------------
    /**
     * Get the text for the selected language
     */
    public GetSpecificTextMap(lang: string, groupKey: string, getOverrides: boolean): LocalizationMap {
        if (!_.has(this.Languages, lang))
            return {};

        let dictionary = (getOverrides) ? this.Languages[lang].DictionaryOverrides : this.Languages[lang].Dictionary;
        if (!_.has(dictionary, groupKey))
            return {};

        return dictionary[groupKey];
    }
    // --------------------------------------------------------------------------------------------
    public GetSupportedLanguages(): { [key: string]: LocalizationLanguageInfo } {
        let info = {};
        _.forOwn(this.Languages, (l) => {
            info[l.Info.Code] = l.Info;
        });
        return info;
    }
    // --------------------------------------------------------------------------------------------
    public SetLanguageInfo(info: LocalizationLanguageInfo) {
        let lang = this.Languages[info.Code];
        if (isDefined(lang)) {
            lang.Info = info;
        } else {
            this.Languages[info.Code] = {
                Dictionary: {},
                DictionaryOverrides: {},
                Info: info
            }
        }
    }
    // --------------------------------------------------------------------------------------------
    public GetLanguageGroups(code: string) {
        let lang = this.Languages[code];
        if (isDefined(lang)) {
            return _.keys(lang.Dictionary);
        }
        return null;
    }
    // --------------------------------------------------------------------------------------------
    public DeleteLanguage(code: string) {
        if (code == this.DefaultLanguage) {
            DebugEx.TraceError("Try to delate the default language.");
            return false;
        }

        // check if we delete the selected language
        if (code == this.SelectedLanguage) {
            let req: LocalizationChangeLanguageReq = { Language: this.DefaultLanguage, ReloadBoard: true };
            this.ChangeSelectedLanguage(req);
        }

        delete this.Languages[code];
        this.InvalidCacheLang(code);
        return true;
    }
    // --------------------------------------------------------------------------------------------
    public GetLanguageDictionary(code: string): LocalizationDictionary {
        return this.Languages[code];
    }
    // --------------------------------------------------------------------------------------------
    public GetLanguages() {
        return this.Languages;
    }
    // --------------------------------------------------------------------------------------------
    public UpdateTranslatedLanguage(code: string, dictionary: LocalizationDictionary) {
        if (code == LocalizationDefaultKey) {
            this.Languages[code].Info = dictionary.Info;
            this.Languages[code].DictionaryOverrides = dictionary.DictionaryOverrides;
            return false;
        }

        // Add/Update language translation
        this.Languages[code] = dictionary;
        this.InvalidCacheLang(code);
        return true;
    }
    // --------------------------------------------------------------------------------------------
}
// --------------------------------------------------------------------------------------------